import { useCallback, useEffect, useState } from 'react';
import { contractsService } from '../services/firestore';
import contactsService from '../services/contactsService';
import { normalizePlate as sharedNormalizePlate } from '../services/possessionMatcher';
import { contractCategory } from '../pages/ContractsManagement/contracts.logic';
import logger from '../utils/logger';
import { USE_CONTRACTS_V2 } from '../services/contractsV2';

/*
 * What each car is doing now (Fleet Manager remarks / summary, Daily Rental Report cars summary).
 *
 *   const { usageOf, ready } = useFleetUsage(cars, { contracts });
 *   usageOf(car) -> 'Monthly' | 'Daily' | 'Invygo' | 'Leasing' | 'Airport' | 'Rented' | null
 *
 * - The car's Ejar status says whether it is rented (null = not rented).
 * - Its open Business Bay contract gives the category (same rule as the Booking column).
 * - A rented car at an airport branch without a Business Bay contract is on an Airport contract.
 * - A car swapped during a contract is not the contract's "Plate No." (an airport customer moved
 *   from Z 60540 to G 47663): its latest E-Check names the contract it is on now.
 * `contracts` (optional): rows already loaded by the page; otherwise the 24h contracts cache is read.
 */
const plateKey = (plate) => sharedNormalizePlate(plate).toLowerCase();
const isRented = (car) => /rent/i.test(String(car?.status || ''));
const atAirport = (branch) => /airport|terminal/i.test(String(branch || ''));

export default function useFleetUsage(cars, { contracts } = {}) {
    const [rentals, setRentals] = useState(null);             // Map plate -> category
    const [openByContract, setOpenByContract] = useState(null); // Map contractNo -> category
    const [lastCheck, setLastCheck] = useState(null);         // Map plate -> { contractNo, branch }

    useEffect(() => {
        let alive = true;
        const build = (rows) => {
            const map = new Map();
            const byNo = new Map();
            (rows || []).forEach((r) => {
                if (!/^open$/i.test(String(r.Status || r.status || '').trim())) return;
                const category = contractCategory(r) || 'Monthly';
                const plate = plateKey(r['Plate No.'] || r.plate_no || '');
                if (plate) map.set(plate, category);
                const no = String(r['Contract No.'] || r.contract_no || '').trim().toUpperCase();
                if (no) byNo.set(no, category);
            });
            if (alive) { setRentals(map); setOpenByContract(byNo); }
        };
        if (contracts) build(contracts);
        else {
            contractsService.getAllContracts()
                .then(build)
                .catch((err) => { logger.error('Could not load open contracts for the fleet', err); if (alive) { setRentals(new Map()); setOpenByContract(new Map()); } });
        }
        return () => { alive = false; };
    }, [contracts]);

    useEffect(() => {
        if (!rentals || lastCheck || !cars || cars.length === 0) return undefined;
        const unmatched = cars.some((c) => c.rta_registered === true && isRented(c)
            && !rentals.has(plateKey(c.plate_number)) && !atAirport(c.branch));
        if (!unmatched) return undefined;
        let alive = true;
        (async () => {
            try {
                if (USE_CONTRACTS_V2) {
                    // the store: the latest check of each rented car without an open Business Bay contract, and
                    // those contracts in the contacts memory (usageOf peeks at their branch / dates)
                    const { latestCheckByPlate } = await import('../services/storeData');
                    const plates = cars.filter((c) => c.rta_registered === true && isRented(c)
                        && !rentals.has(plateKey(c.plate_number)) && !atAirport(c.branch)).map((c) => c.plate_number);
                    const found = await latestCheckByPlate(plates);
                    const latest = new Map();
                    found.forEach((r, plate) => {
                        const contractNo = String(r['Contract No.'] || '').trim().toUpperCase();
                        if (contractNo) latest.set(plateKey(plate), { at: r._at || 0, contractNo, branch: String(r.Branch || '') });
                    });
                    await contactsService.prefetch([...latest.values()].map((x) => x.contractNo));
                    if (alive) setLastCheck(latest);
                    return;
                }
                const { archiveService, parseArchiveDate } = await import('../services/archiveService');
                const rows = await archiveService.load();
                const latest = new Map();
                (rows || []).forEach((r) => {
                    const plate = plateKey(r['Plate Number'] || r['Plate No.'] || '');
                    const contractNo = String(r['Contract No.'] || r['Contract No'] || '').trim().toUpperCase();
                    if (!plate || !contractNo) return;
                    const at = parseArchiveDate(r['Transaction Date']).getTime();
                    const prev = latest.get(plate);
                    if (!prev || at > prev.at) latest.set(plate, { at, contractNo, branch: String(r.Branch || '') });
                });
                if (alive) setLastCheck(latest);
            } catch (err) {
                logger.error('Could not read E-Checks for the fleet', err);
                if (alive) setLastCheck(new Map());
            }
        })();
        return () => { alive = false; };
    }, [rentals, cars, lastCheck]);

    const usageOf = useCallback((car) => {
        if (!car || !isRented(car)) return null;
        const plate = plateKey(car.plate_number);
        const byContract = rentals ? rentals.get(plate) : null;
        if (byContract) return byContract;
        if (atAirport(car.branch)) return 'Airport';
        const check = lastCheck ? lastCheck.get(plate) : null;
        if (check) {
            const open = openByContract ? openByContract.get(check.contractNo) : null;
            if (open) return open;
            if (atAirport(check.branch)) return 'Airport';
            const row = contactsService.peek(check.contractNo);
            if (atAirport(row?.b)) return 'Airport';
            const category = contractCategory({ contractNo: check.contractNo, pickupDate: row?.d, dropoffDate: row?.e, pickupBranch: row?.b });
            if (category) return category;
        }
        return 'Rented';
    }, [rentals, openByContract, lastCheck]);

    return { usageOf, ready: !!rentals };
}
